import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import {
  X,
  AlertTriangle,
  AlertCircle,
  Plus,
  Trash2,
  CheckCircle2,
  Loader2,
  Package,
  Check,
} from 'lucide-react'
import {
  getMaterialProposal,
  confirmMaterialProposal,
  type MaterialProposalLine,
} from './api'

interface MaterialProposalModalProps {
  proposalId: string
  title?: string
  initialLines?: MaterialProposalLine[]
  initialWarnings?: string[]
  initialResult?: Array<{ id?: string; code: string; name: string }>
  onClose: () => void
  onConfirmed: (items: Array<{ id?: string; code: string; name: string }>) => void
}

interface AutoResizeTextareaProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  title?: string
  ariaLabel?: string
  minRows?: number
}

const AutoResizeTextarea: React.FC<AutoResizeTextareaProps> = ({
  value,
  onChange,
  placeholder,
  className = '',
  title,
  ariaLabel,
  minRows = 1,
}) => {
  const ref = useRef<HTMLTextAreaElement>(null)

  const adjustHeight = useCallback(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    const newHeight = Math.max(28, el.scrollHeight)
    el.style.height = `${newHeight}px`
  }, [])

  useEffect(() => {
    adjustHeight()
  }, [value, adjustHeight])

  return (
    <textarea
      ref={ref}
      rows={minRows}
      value={value}
      title={title ?? value}
      aria-label={ariaLabel}
      onChange={(e) => {
        onChange(e.target.value)
        adjustHeight()
      }}
      placeholder={placeholder}
      className={`w-full resize-none overflow-hidden rounded px-2 py-1 text-xs border border-transparent hover:border-slate-300 focus:border-[var(--color-primary)] focus:bg-white focus:outline-none transition-colors whitespace-pre-wrap break-words leading-snug ${className}`}
    />
  )
}

const COMMON_UOMS = ['Tấm', 'Thanh', 'Cái', 'Bộ', 'Kg', 'm', 'm2', 'm3', 'Cuộn', 'Hộp', 'Lít']

export const MaterialProposalModal: React.FC<MaterialProposalModalProps> = ({
  proposalId,
  title: initialTitle,
  initialLines,
  initialWarnings,
  initialResult,
  onClose,
  onConfirmed,
}) => {
  const [lines, setLines] = useState<MaterialProposalLine[]>(initialLines ?? [])
  const [title, setTitle] = useState<string>(initialTitle || 'Đề xuất Tạo Vật tư Mới')
  const [proposalWarnings, setProposalWarnings] = useState<string[]>(initialWarnings ?? [])
  const [isLoading, setIsLoading] = useState(!initialLines && !initialResult)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [createdResult, setCreatedResult] = useState<Array<{ id?: string; code: string; name: string }> | null>(
    initialResult ?? null,
  )

  // Fetch proposal details on mount if initialLines not provided
  useEffect(() => {
    if (initialLines || initialResult) return

    let cancelled = false
    setIsLoading(true)
    setLoadError(null)

    getMaterialProposal(proposalId)
      .then((res) => {
        if (cancelled) return
        const pData = res.data
        if (pData.title) setTitle(pData.title)
        if (pData.warnings) setProposalWarnings(pData.warnings)

        const rawMaterials = pData.data?.materials || (pData as any).lines || (pData as any).materials || []
        // Check if already confirmed
        if (pData.status === 'CONFIRMED' && pData.result?.created_materials) {
          setCreatedResult(pData.result.created_materials)
        }
        setLines(rawMaterials)
      })
      .catch((err) => {
        if (cancelled) return
        setLoadError(err instanceof Error ? err.message : 'Không thể tải đề xuất vật tư.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [proposalId, initialLines, initialResult])

  const handleUpdateLine = (index: number, patch: Partial<MaterialProposalLine>) => {
    setLines((prev) => {
      const copy = [...prev]
      const current = { ...copy[index], ...patch }

      // Re-validate row on change
      const errors: string[] = []
      if (!current.name?.trim()) {
        errors.push('Tên vật tư bắt buộc')
      }
      if (!current.uom_default?.trim()) {
        errors.push('ĐVT bắt buộc')
      }
      if (current.kind !== 'RAW' && current.kind !== 'AUX') {
        errors.push('Loại vật tư phải là RAW hoặc AUX')
      }

      current.errors = errors
      copy[index] = current
      return copy
    })
  }

  const handleDeleteLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index))
  }

  const handleAddLine = () => {
    const newLine: MaterialProposalLine = {
      id: `line-manual-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: '',
      spec: '',
      uom_default: 'Tấm',
      group_code: 'van',
      kind: 'RAW',
      code: '',
      selected: true,
      is_duplicate: false,
      errors: ['Tên vật tư bắt buộc'],
      warnings: [],
    }
    setLines((prev) => [...prev, newLine])
  }

  const handleToggleSelectAll = (checked: boolean) => {
    setLines((prev) =>
      prev.map((l) => ({
        ...l,
        selected: checked,
      })),
    )
  }

  // Statistics
  const selectedCount = useMemo(() => lines.filter((l) => l.selected !== false).length, [lines])
  const duplicateCount = useMemo(() => lines.filter((l) => l.is_duplicate).length, [lines])
  const errorCount = useMemo(
    () =>
      lines.filter((l) => {
        if (l.selected === false) return false
        return !l.name?.trim() || !l.uom_default?.trim() || (l.errors && l.errors.length > 0)
      }).length,
    [lines],
  )

  // Blocking validation: any selected line missing name/uom or with errors
  const blocking = useMemo(() => {
    if (selectedCount === 0) return true
    return lines.some((l) => {
      if (l.selected === false) return false
      return !l.name?.trim() || !l.uom_default?.trim() || (l.errors && l.errors.length > 0)
    })
  }, [lines, selectedCount])

  const handleConfirm = async () => {
    if (blocking || isSubmitting) return

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Map to server confirm input
      const materialsToConfirm = lines.map((l) => ({
        name: l.name.trim(),
        code: l.code?.trim() || null,
        spec: l.spec?.trim() || null,
        uom_default: l.uom_default.trim(),
        group_code: l.group_code || null,
        kind: l.kind || 'RAW',
        notes: l.notes || null,
        selected: l.selected !== false,
        map_to_quote_line_idx: l.map_to_quote_line_idx ?? null,
      }))

      const res = await confirmMaterialProposal(proposalId, materialsToConfirm)
      const created = res.result?.created_materials || []
      setCreatedResult(created)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi xác nhận tạo vật tư.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-label="Bảng xem trước & Xác nhận Đề xuất Tạo Vật tư"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4 print:hidden backdrop-blur-xs font-sans"
    >
      <div className="flex flex-col h-[85vh] max-h-[85vh] w-[96vw] max-w-6xl rounded-xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header - Fixed top */}
        <div className="shrink-0 flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--color-primary)] text-white shadow-xs">
              <Package className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-slate-800">{title}</h2>
                <span className="rounded bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
                  AI Đề xuất Tạo Vật tư
                </span>
                <span className="text-xs text-slate-400 font-mono">({proposalId})</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Kiểm tra, điều chỉnh thông tin các vật tư trước khi tạo vào hệ thống. Các dòng trùng lặp được mặc định bỏ qua.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Đóng"
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Success View after Confirmation */}
        {createdResult ? (
          <div className="flex-1 overflow-auto p-6 flex flex-col items-center justify-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-4">
              <CheckCircle2 className="h-10 w-10" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">
              Đã tạo thành công {createdResult.length} vật tư vào hệ thống!
            </h3>
            <p className="text-xs text-slate-500 max-w-md mb-6">
              Các mã vật tư đã được tạo chính thức trong cơ sở dữ liệu ERP Mộc Phát và sẵn sàng sử dụng cho BOM, báo giá và kho.
            </p>

            <div className="w-full max-w-2xl rounded-lg border border-slate-200 bg-slate-50/50 p-4 overflow-hidden mb-6">
              <div className="max-h-60 overflow-y-auto">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-slate-200 bg-slate-100 text-[11px] font-semibold uppercase text-slate-600 sticky top-0">
                    <tr>
                      <th className="p-2 w-12 text-center">STT</th>
                      <th className="p-2 w-36">Mã vật tư</th>
                      <th className="p-2">Tên vật tư</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {createdResult.map((m, idx) => (
                      <tr key={m.code || idx}>
                        <td className="p-2 text-center text-slate-500">{idx + 1}</td>
                        <td className="p-2 font-mono font-semibold text-emerald-700">{m.code}</td>
                        <td className="p-2 text-slate-800">{m.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                if (createdResult) onConfirmed(createdResult)
                onClose()
              }}
              className="rounded-lg bg-[var(--color-primary)] px-6 py-2.5 text-xs font-semibold text-white shadow-xs hover:bg-[var(--color-primary-hover)] transition-all cursor-pointer"
            >
              Hoàn tất & Đóng
            </button>
          </div>
        ) : (
          <>
            {/* Stats Toolbar & Warnings */}
            <div className="shrink-0 border-b border-slate-200 bg-white px-6 py-3 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3 text-xs">
                <span className="font-medium text-slate-600">
                  Tổng cộng: <strong>{lines.length}</strong> dòng
                </span>
                <span className="rounded bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-emerald-700">
                  Chọn tạo: <strong>{selectedCount}</strong>
                </span>
                {duplicateCount > 0 && (
                  <span className="rounded bg-amber-50 border border-amber-200 px-2 py-0.5 text-amber-700">
                    Trùng lặp: <strong>{duplicateCount}</strong>
                  </span>
                )}
                {errorCount > 0 && (
                  <span className="rounded bg-rose-50 border border-rose-200 px-2 py-0.5 text-rose-700 font-medium">
                    Lỗi chặn: <strong>{errorCount}</strong>
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleAddLine}
                  className="flex items-center gap-1.5 rounded-md border border-[var(--color-primary)] bg-white px-3 py-1.5 text-xs font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary-soft)]/20 shadow-2xs transition-colors cursor-pointer"
                >
                  <Plus className="h-3.5 w-3.5" />
                  <span>Thêm dòng vật tư</span>
                </button>
              </div>
            </div>

            {/* Proposal Warning Notice (e.g. OCR fallback) */}
            {proposalWarnings.length > 0 && (
              <div className="shrink-0 bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center gap-2 text-xs text-amber-800 flex-wrap">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                <span>{proposalWarnings.join('; ')}</span>
              </div>
            )}

            {/* Table Area */}
            <div className="flex-1 overflow-auto bg-slate-100 p-3 sm:p-4">
              {isLoading ? (
                <div className="flex h-64 items-center justify-center gap-2 text-xs text-slate-500">
                  <Loader2 className="h-5 w-5 animate-spin text-[var(--color-primary)]" />
                  <span>Đang tải thông tin đề xuất vật tư...</span>
                </div>
              ) : loadError ? (
                <div className="p-6 text-center text-rose-600 text-xs">{loadError}</div>
              ) : lines.length === 0 ? (
                <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-400 text-xs">
                  Không có dòng vật tư nào trong đề xuất. Nhấn &quot;Thêm dòng vật tư&quot; để bổ sung.
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 bg-white shadow-xs overflow-x-auto">
                  <table className="text-left text-xs text-slate-700 border-collapse table-fixed w-full min-w-[1050px]">
                    <colgroup>
                      <col style={{ width: 44 }} />
                      <col style={{ width: 48 }} />
                      <col style={{ width: 230 }} />
                      <col style={{ width: 140 }} />
                      <col style={{ width: 85 }} />
                      <col style={{ width: 125 }} />
                      <col style={{ width: 120 }} />
                      <col style={{ width: 120 }} />
                      <col style={{ width: 190 }} />
                      <col style={{ width: 44 }} />
                    </colgroup>
                    <thead className="bg-slate-100 text-[11px] font-semibold uppercase tracking-wider text-slate-600 border-b border-slate-200 sticky top-0 z-20 shadow-2xs">
                      <tr>
                        <th
                          style={{ position: 'sticky', left: 0, zIndex: 30, backgroundColor: '#f1f5f9' }}
                          className="px-2 py-2 text-center border-r border-slate-200 shadow-r"
                        >
                          STT
                        </th>
                        <th className="px-2 py-2 text-center border-r border-slate-200">
                          <input
                            type="checkbox"
                            checked={lines.length > 0 && lines.every((l) => l.selected !== false)}
                            onChange={(e) => handleToggleSelectAll(e.target.checked)}
                            title="Chọn / Bỏ chọn tất cả"
                            aria-label="Chọn tất cả dòng"
                          />
                        </th>
                        <th className="px-2.5 py-2 border-r border-slate-200">Tên vật tư *</th>
                        <th className="px-2.5 py-2 border-r border-slate-200">Quy cách</th>
                        <th className="px-2.5 py-2 text-center border-r border-slate-200">ĐVT *</th>
                        <th className="px-2.5 py-2 border-r border-slate-200">Nhóm</th>
                        <th className="px-2.5 py-2 border-r border-slate-200">Loại</th>
                        <th className="px-2.5 py-2 border-r border-slate-200">Mã vật tư</th>
                        <th className="px-2.5 py-2 border-r border-slate-200">Trạng thái / Đối chiếu</th>
                        <th className="px-2 py-2 text-center">Xóa</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {lines.map((line, idx) => {
                        const isDup = line.is_duplicate
                        const isErr = (line.errors?.length ?? 0) > 0 || !line.name?.trim() || !line.uom_default?.trim()
                        const rowBg = isDup
                          ? 'bg-amber-50/40 hover:bg-amber-50/70'
                          : isErr
                          ? 'bg-rose-50/40 hover:bg-rose-50/70'
                          : 'bg-white hover:bg-slate-50/80'
                        const sttBg = isDup ? '#fef3c7' : isErr ? '#ffe4e6' : '#ffffff'

                        return (
                          <tr key={line.id || idx} className={`${rowBg} transition-colors align-top`}>
                            {/* STT */}
                            <td
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 10,
                                backgroundColor: sttBg,
                              }}
                              className="px-2 py-2 text-center font-mono text-slate-500 border-r border-slate-100 shadow-r"
                            >
                              {idx + 1}
                            </td>

                            {/* Checkbox Tạo */}
                            <td className="px-2 py-2 text-center border-r border-slate-100">
                              <input
                                type="checkbox"
                                checked={line.selected !== false}
                                onChange={(e) => handleUpdateLine(idx, { selected: e.target.checked })}
                                aria-label={`Chọn tạo dòng ${idx + 1}`}
                                className="h-4 w-4 rounded border-slate-300 text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
                              />
                            </td>

                            {/* Tên vật tư */}
                            <td className="px-2 py-1.5 border-r border-slate-100">
                              <AutoResizeTextarea
                                value={line.name}
                                onChange={(val) => handleUpdateLine(idx, { name: val })}
                                placeholder="Nhập tên vật tư..."
                                ariaLabel={`Tên vật tư dòng ${idx + 1}`}
                                className={!line.name?.trim() ? 'border-rose-400 bg-rose-50/40' : ''}
                              />
                            </td>

                            {/* Quy cách */}
                            <td className="px-2 py-1.5 border-r border-slate-100">
                              <AutoResizeTextarea
                                value={line.spec || line.quy_cach || ''}
                                onChange={(val) => handleUpdateLine(idx, { spec: val, quy_cach: val })}
                                placeholder="Quy cách..."
                                ariaLabel={`Quy cách dòng ${idx + 1}`}
                              />
                            </td>

                            {/* ĐVT */}
                            <td className="px-2 py-1.5 border-r border-slate-100 text-center">
                              <input
                                type="text"
                                list="uom-options"
                                value={line.uom_default || ''}
                                onChange={(e) => handleUpdateLine(idx, { uom_default: e.target.value })}
                                placeholder="ĐVT..."
                                aria-label={`ĐVT dòng ${idx + 1}`}
                                className={`w-full rounded px-1.5 py-1 text-xs text-center border ${
                                  !line.uom_default?.trim()
                                    ? 'border-rose-400 bg-rose-50/40'
                                    : 'border-transparent hover:border-slate-300 focus:border-[var(--color-primary)] focus:bg-white'
                                } focus:outline-none`}
                              />
                              <datalist id="uom-options">
                                {COMMON_UOMS.map((u) => (
                                  <option key={u} value={u} />
                                ))}
                              </datalist>
                            </td>

                            {/* Nhóm */}
                            <td className="px-2 py-1.5 border-r border-slate-100">
                              <select
                                value={line.group_code || ''}
                                onChange={(e) => handleUpdateLine(idx, { group_code: e.target.value || null })}
                                aria-label={`Nhóm vật tư dòng ${idx + 1}`}
                                className="w-full rounded border border-transparent hover:border-slate-300 focus:border-[var(--color-primary)] focus:bg-white px-2 py-1 text-xs text-slate-700 bg-transparent focus:outline-none"
                              >
                                <option value="">-- Chưa chọn --</option>
                                <option value="go">Phôi gỗ (go)</option>
                                <option value="van">Ván CN (van)</option>
                                <option value="nk">Ngũ kim (nk)</option>
                                <option value="bb">Bao bì (bb)</option>
                                <option value="td">Thông dụng (td)</option>
                              </select>
                            </td>

                            {/* Loại */}
                            <td className="px-2 py-1.5 border-r border-slate-100">
                              <select
                                value={line.kind || 'RAW'}
                                onChange={(e) => handleUpdateLine(idx, { kind: e.target.value as 'RAW' | 'AUX' })}
                                aria-label={`Loại vật tư dòng ${idx + 1}`}
                                className="w-full rounded border border-transparent hover:border-slate-300 focus:border-[var(--color-primary)] focus:bg-white px-2 py-1 text-xs text-slate-700 bg-transparent focus:outline-none"
                              >
                                <option value="RAW">Nguyên liệu chính</option>
                                <option value="AUX">Phụ liệu / Phụ kiện</option>
                              </select>
                            </td>

                            {/* Mã vật tư */}
                            <td className="px-2 py-1.5 border-r border-slate-100">
                              <input
                                type="text"
                                value={line.code || ''}
                                onChange={(e) => handleUpdateLine(idx, { code: e.target.value })}
                                placeholder="(Tự động sinh)"
                                aria-label={`Mã vật tư dòng ${idx + 1}`}
                                className="w-full rounded px-2 py-1 text-xs font-mono border border-transparent hover:border-slate-300 focus:border-[var(--color-primary)] focus:bg-white focus:outline-none"
                              />
                            </td>

                            {/* Trạng thái / Đối chiếu */}
                            <td className="px-2.5 py-1.5 border-r border-slate-100 whitespace-normal break-words leading-tight">
                              {isDup ? (
                                <div className="space-y-1">
                                  <div className="flex items-start gap-1 text-[11px] font-medium text-amber-800">
                                    <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                                    <span>
                                      Đã có: {line.existing_material?.code || line.duplicate_material?.code || ''}{' '}
                                      {line.existing_material?.name || line.duplicate_material?.name || ''}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5 text-[10.5px]">
                                    <button
                                      type="button"
                                      onClick={() => handleUpdateLine(idx, { selected: false })}
                                      className={`rounded px-1.5 py-0.5 text-xs transition-colors ${
                                        line.selected === false
                                          ? 'bg-amber-200 text-amber-900 font-semibold'
                                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                      }`}
                                    >
                                      Bỏ qua
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleUpdateLine(idx, { selected: true })}
                                      className={`rounded px-1.5 py-0.5 text-xs transition-colors ${
                                        line.selected === true
                                          ? 'bg-amber-600 text-white font-semibold'
                                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                      }`}
                                    >
                                      Tạo mới
                                    </button>
                                  </div>
                                </div>
                              ) : isErr ? (
                                <div className="flex items-start gap-1 text-[11px] font-medium text-rose-700">
                                  <AlertCircle className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" />
                                  <span>
                                    {line.errors?.join('; ') || (!line.name?.trim() ? 'Thiếu tên' : 'Thiếu ĐVT')}
                                  </span>
                                </div>
                              ) : (
                                <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                                  <Check className="h-3 w-3" />
                                  <span>Hợp lệ</span>
                                </span>
                              )}
                            </td>

                            {/* Xóa dòng */}
                            <td className="px-2 py-1.5 text-center">
                              <button
                                type="button"
                                onClick={() => handleDeleteLine(idx)}
                                aria-label={`Xóa dòng ${idx + 1}`}
                                className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Error Message if submit fails */}
            {submitError && (
              <div role="alert" className="shrink-0 bg-rose-50 border-t border-rose-200 px-6 py-2.5 text-xs text-rose-800">
                {submitError}
              </div>
            )}

            {/* Footer Actions - Fixed Bottom */}
            <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-6 py-3 flex items-center justify-between">
              <div className="text-xs">
                {blocking ? (
                  <span className="text-rose-600 font-medium">
                    {selectedCount === 0
                      ? 'Vui lòng chọn ít nhất 1 dòng vật tư để tạo.'
                      : 'Có dòng được chọn đang thiếu thông tin bắt buộc (Tên hoặc ĐVT). Vui lòng sửa hoặc bỏ chọn.'}
                  </span>
                ) : (
                  <span className="text-emerald-700 font-medium">
                    Sẵn sàng tạo {selectedCount} vật tư mới vào hệ thống.
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-2xs transition-colors cursor-pointer"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  disabled={blocking || isSubmitting || lines.length === 0}
                  onClick={handleConfirm}
                  className="rounded-lg bg-[var(--color-primary)] px-5 py-2 text-xs font-semibold text-white shadow-xs hover:bg-[var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 transition-all cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Đang tạo vật tư...</span>
                    </>
                  ) : (
                    <span>Xác nhận tạo {selectedCount} vật tư</span>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
